import React, { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Wrench,
  Bot,
  Book,
  ListOrdered,
  MessageCircle,
  LogOut,
  User,
  Settings,
  Shield,
  ChevronDown,
  Menu,
  X,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import Logo from "../assets/logo-2.png";

const navigationItems = [
  { id: "tools", label: "Tools", icon: Wrench, path: "/studio/tools" },
  { id: "agents", label: "Agents", icon: Bot, path: "/studio/agents" },
  { id: "playbooks", label: "Playbooks", icon: Book, path: "/studio/playbooks" },
  {
    id: "interactive-playbooks",
    label: "Dialogues",
    icon: MessageCircle,
    path: "/studio/interactive-playbooks",
  },
  {
    id: "executions",
    label: "Executions",
    icon: ListOrdered,
    path: "/studio/executions",
  },
];

const adminItems = [
  { id: "admin-dashboard", label: "Admin Dashboard", path: "/studio/admin" },
  { id: "core-items", label: "Core Items", path: "/studio/admin/core-items" },
  { id: "users", label: "User Management", path: "/studio/admin/users" },
  { id: "adapters", label: "Adapters", path: "/studio/admin/adapters" },
  { id: "webhooks", label: "Webhooks", path: "/studio/admin/webhooks" },
  { id: "rag", label: "Agentic RAG", path: "rag" },
];

const TopBar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signOut, isAdmin } = useAuth();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [adminMenuOpen, setAdminMenuOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const adminMenuRef = useRef<HTMLDivElement>(null);

  const isActivePath = (path: string): boolean =>
    location.pathname.startsWith(path);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        userMenuRef.current &&
        !userMenuRef.current.contains(event.target as Node)
      ) {
        setUserMenuOpen(false);
      }
      if (
        adminMenuRef.current &&
        !adminMenuRef.current.contains(event.target as Node)
      ) {
        setAdminMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleNavigation = (path: string) => {
    if (path === "rag" && user) {
      const isAdmin = user.role === "ADMIN" ? "true" : "false";
      const baseUrl = process.env.REACT_APP_RAG_BASE_URL;
      const nextUrl = `${baseUrl}/handshake?uuid=${encodeURIComponent(user?.userId)}&is_admin=${isAdmin}`;
      window.open(nextUrl, "_blank", "noopener,noreferrer");
      return;
    }
    navigate(path);
    setMobileMenuOpen(false);
    setUserMenuOpen(false);
    setAdminMenuOpen(false);
  };

  return (
    <>
      <div className="fixed top-0 left-0 right-0 bg-white border-b border-gray-200 z-50">
        <div className="max-w-[1920px] mx-auto">
          <div className="flex items-center justify-between h-16 px-4 lg:px-6">
            {/* Logo */}
            <div className="flex items-center">
              <img
                src={Logo}
                alt="AgenticGenie Logo"
                className="h-8 w-auto cursor-pointer"
                onClick={() => handleNavigation("/studio/tools")}
              />
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-1">
              {navigationItems.map((item) => {
                const Icon = item.icon;
                const active = isActivePath(item.path);
                return (
                  <button
                    key={item.id}
                    onClick={() => handleNavigation(item.path)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 ${
                      active
                        ? "bg-blue-50 text-blue-700 shadow-sm"
                        : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                    }`}
                  >
                    <Icon size={18} />
                    <span>{item.label}</span>
                  </button>
                );
              })}
            </nav>

            {/* Right Side - Admin & User Menu */}
            <div className="flex items-center space-x-3">
              {/* Admin Menu */}
              {isAdmin && (
                <div className="relative" ref={adminMenuRef}>
                  <button
                    onClick={() => setAdminMenuOpen(!adminMenuOpen)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg font-medium text-sm transition-all duration-200 ${
                      location.pathname.startsWith("/studio/admin")
                        ? "bg-purple-50 text-purple-700 shadow-sm"
                        : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                    }`}
                  >
                    <Shield size={18} />
                    <span className="hidden md:inline">Admin</span>
                    <ChevronDown
                      size={16}
                      className={`transition-transform ${adminMenuOpen ? "rotate-180" : ""}`}
                    />
                  </button>

                  {adminMenuOpen && (
                    <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                      {adminItems.map((item) => (
                        <button
                          key={item.id}
                          onClick={() => handleNavigation(item.path)}
                          className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                            isActivePath(item.path)
                              ? "bg-purple-50 text-purple-700"
                              : "text-gray-700 hover:bg-gray-50"
                          }`}
                        >
                          {item.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* User Profile Menu */}
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="bg-blue-600 text-white font-semibold rounded-full w-8 h-8 flex items-center justify-center text-sm">
                    {user?.firstName?.[0]?.toUpperCase() ||
                      user?.lastName?.[0]?.toUpperCase() ||
                      user?.email?.[0]?.toUpperCase() ||
                      "U"}
                  </div>
                  <ChevronDown
                    size={16}
                    className={`transition-transform hidden sm:block ${userMenuOpen ? "rotate-180" : ""}`}
                  />
                </button>

                {userMenuOpen && (
                  <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                    {/* User Info */}
                    <div className="px-4 py-3 border-b border-gray-100">
                      <div className="flex items-center gap-3">
                        <div className="bg-blue-600 text-white font-semibold rounded-full w-10 h-10 flex items-center justify-center">
                          {user?.firstName?.[0]?.toUpperCase() ||
                            user?.lastName?.[0]?.toUpperCase() ||
                            user?.email?.[0]?.toUpperCase() ||
                            "U"}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {user?.firstName} {user?.lastName}
                          </p>
                          <p className="text-xs text-gray-500 truncate">
                            {user?.email}
                          </p>
                          {isAdmin && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800 mt-1">
                              Admin
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Menu Items */}
                    {/* <div className="py-1">
                      <button
                        onClick={() => {
                          // TODO: Add profile/settings functionality
                          setUserMenuOpen(false);
                        }}
                        className="flex items-center gap-3 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        <User size={16} />
                        Profile Settings
                      </button>
                      <button
                        onClick={() => {
                          // TODO: Add preferences functionality
                          setUserMenuOpen(false);
                        }}
                        className="flex items-center gap-3 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        <Settings size={16} />
                        Preferences
                      </button>
                    </div> */}

                    {/* Logout */}
                    <div className="border-t border-gray-100 pt-1">
                      <button
                        onClick={() => {
                          signOut();
                          setUserMenuOpen(false);
                        }}
                        className="flex items-center gap-3 w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                      >
                        <LogOut size={16} />
                        Sign Out
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden p-2 rounded-lg hover:bg-gray-50 transition-colors"
              >
                {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div
            className="fixed inset-0 bg-black bg-opacity-25"
            onClick={() => setMobileMenuOpen(false)}
          />
          <div className="fixed top-16 left-0 right-0 bg-white border-b border-gray-200 shadow-lg">
            <nav className="px-4 py-4 space-y-2">
              {navigationItems.map((item) => {
                const Icon = item.icon;
                const active = isActivePath(item.path);
                return (
                  <button
                    key={item.id}
                    onClick={() => handleNavigation(item.path)}
                    className={`flex items-center gap-3 w-full px-4 py-3 rounded-lg font-medium text-sm transition-all duration-200 ${
                      active
                        ? "bg-blue-50 text-blue-700"
                        : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                    }`}
                  >
                    <Icon size={20} />
                    <span>{item.label}</span>
                  </button>
                );
              })}

              {/* Admin items for mobile */}
              {isAdmin && (
                <>
                  <div className="border-t border-gray-200 pt-2 mt-4">
                    <p className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Admin
                    </p>
                  </div>
                  {adminItems.map((item) => {
                    const active = isActivePath(item.path);
                    return (
                      <button
                        key={item.id}
                        onClick={() => handleNavigation(item.path)}
                        className={`flex items-center gap-3 w-full px-4 py-3 rounded-lg font-medium text-sm transition-all duration-200 ${
                          active
                            ? "bg-purple-50 text-purple-700"
                            : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                        }`}
                      >
                        <Shield size={20} />
                        <span>{item.label}</span>
                      </button>
                    );
                  })}
                </>
              )}
            </nav>
          </div>
        </div>
      )}
    </>
  );
};

export default TopBar;
